import { ChangeEvent, useCallback, useState } from 'react';
import isNumeric from 'validator/lib/isNumeric';

import {
  ViewProps,
  ANSWER_FIELD,
  ERROR_ELEMENT_ID,
} from './equation-response.constants';
import * as Styled from './equation-response-view.style';
import { getOperatorSymbol } from './equation-response.utils';
import { FlexColumn } from 'approot/shared/styles/flex.style';
import { COMPONENT_LAYOUT_HORIZONTAL } from 'global/constants';
import { spacing16, spacing40 } from 'approot/shared/styles/spacing.style';
import ErrorMessageStyled from 'approot/shared/error-message/error-message.style';
import { EquationResponseHr } from './equation-response.style';
import SubActivityView from '../sub-activity/sub-activity-view';
import { setComponentAnswer } from 'approot/activity-builder/check-answer/check-answer.utils';
import { useCheckAnswer } from 'approot/activity-builder/check-answer/check-answer.hooks';

const EquationResponseView = ({
  id,
  properties,
  subActivityProperties,
  sectionId,
}: ViewProps) => {
  const [error, setError] = useState<boolean>(false);

  const { checkAnswerData, currentAnswerData } = useCheckAnswer({
    componentId: id,
  });

  const correctAnswer = currentAnswerData?.correctAnswer;
  const checkedAnswer = checkAnswerData[sectionId]?.hasChecked;

  const accessibleLabel = `What is the result of: ${properties.firstOperand} ${properties.operation} ${properties.secondOperand}`;
  const enteredAnswerNum = currentAnswerData?.answer as number | undefined;
  const isCorrect = enteredAnswerNum === correctAnswer;
  const isHorizontal =
    properties.layout?.orientation === COMPONENT_LAYOUT_HORIZONTAL;

  const handleOnChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (isNumeric(event.target.value)) {
        setComponentAnswer({
          sectionId,
          componentId: id,
          answer: Number(event.target.value),
          checkAnswerData,
        });
        setError(false);
      } else {
        setComponentAnswer({
          sectionId,
          componentId: id,
          answer: undefined,
          checkAnswerData,
        });
        setError(true);
      }
    },
    [checkAnswerData, id, sectionId]
  );

  return (
    <SubActivityView subActivityDetails={subActivityProperties}>
      <form noValidate onSubmit={e => e.preventDefault()}>
        <FlexColumn>
          <Styled.EquationResponseFormWrapper
            $vertical={!isHorizontal}
            $marginBottom={error ? spacing16 : undefined}
          >
            {isHorizontal ? (
              <>
                <Styled.EquationResponseBox>
                  {properties.firstOperand}
                </Styled.EquationResponseBox>

                <Styled.EquationResponseOperator>
                  {getOperatorSymbol(properties.operation)}
                </Styled.EquationResponseOperator>

                <Styled.EquationResponseBox>
                  {properties.secondOperand}
                </Styled.EquationResponseBox>

                <Styled.EquationResponseOperator>
                  =
                </Styled.EquationResponseOperator>

                <Styled.EquationResponseInputAnswer
                  placeholder="?"
                  readOnly={checkedAnswer}
                  id={ANSWER_FIELD}
                  name={ANSWER_FIELD}
                  value={enteredAnswerNum || ''}
                  onChange={event => handleOnChange(event)}
                  $hasError={error}
                  $isCorrect={checkedAnswer && isCorrect}
                  $isIncorrect={checkedAnswer && !isCorrect}
                  aria-label={accessibleLabel}
                  aria-invalid={error}
                  aria-describedby={ERROR_ELEMENT_ID}
                  data-check-answer-element={true}
                />
              </>
            ) : (
              <>
                <Styled.EquationResponseBoxVertical>
                  {properties.firstOperand}
                </Styled.EquationResponseBoxVertical>

                <Styled.EquationResponseOperatorWrapper>
                  <Styled.EquationResponseOperator>
                    {getOperatorSymbol(properties.operation)}
                  </Styled.EquationResponseOperator>
                  <Styled.EquationResponseBoxVertical>
                    {properties.secondOperand}
                  </Styled.EquationResponseBoxVertical>
                </Styled.EquationResponseOperatorWrapper>

                <EquationResponseHr />

                <Styled.EquationResponseInputAnswerVertical
                  placeholder="?"
                  readOnly={checkedAnswer}
                  id={ANSWER_FIELD}
                  name={ANSWER_FIELD}
                  value={enteredAnswerNum || ''}
                  onChange={event => handleOnChange(event)}
                  $hasError={error}
                  $isCorrect={checkedAnswer && isCorrect}
                  $isIncorrect={checkedAnswer && !isCorrect}
                  aria-label={accessibleLabel}
                  aria-invalid={error}
                  aria-describedby={ERROR_ELEMENT_ID}
                  data-check-answer-element={true}
                />
              </>
            )}
          </Styled.EquationResponseFormWrapper>

          {error && (
            <ErrorMessageStyled id={ERROR_ELEMENT_ID} $marginBottom={spacing40}>
              Please add a numeric value for your response.
            </ErrorMessageStyled>
          )}
        </FlexColumn>
      </form>
    </SubActivityView>
  );
};

export default EquationResponseView;
